import { useMemo } from 'react';
import { Tabs } from '~/components/ui/Tabs';
import { PerspectiveCaption } from '~/components/ui/PerspectiveCaption';
import { useTranslation } from 'react-i18next';
import { OrderList } from '~/pages/orders/sections/OrderList.tsx';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { useGetOrdersCountByPerspective, useGetOrdersPage } from '~/api';
import type { PerspectiveOrder } from '~/api/order';
import { useGetOrganizationBuyers } from '~/api/users';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import type {
  OrderStatus,
  OrderStatusCount,
  TradePerspective,
} from '~/graphql/graphql';
import type { RootState } from '~/redux/store';
import type { PaginatedResponse } from '~/types';
import { RetryBanner } from '~/components/ui/RetryBanner';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { getOrderFilterFields } from '~/pages/orders/lib/orderFilterFields';
import {
  activeRowCount,
  buildOrderFiltersInput,
} from '~/pages/orders/lib/orderFilters';
import { useOrdersSavedViews } from '~/pages/orders/lib/useOrdersSavedViews';
import { OrdersToolbar } from '~/pages/orders/sections/OrdersToolbar';
import { OrdersFilterReadout } from '~/pages/orders/sections/OrdersFilterReadout';

type TabDetail = {
  labelKey: string;
  link: string;
  count: number;
  statusKey?: OrderStatus | undefined;
  noOrdersMessageKey: string;
};
type OrderStatusTabDetails = {
  [key: string]: TabDetail;
};

const ORDERS_PAGE_SIZE = 20;

const perspectiveBasePath: Record<TradePerspective, string> = {
  BUYING: 'buyer-orders',
  SELLING: 'seller-orders',
};

interface OrdersProps {
  perspective: TradePerspective;
}

export default function Orders({ perspective }: Readonly<OrdersProps>) {
  const { t } = useTranslation('order');
  const basePath = perspectiveBasePath[perspective];
  const currentOrderTab = useLocation().pathname.split('/')[2] || '';
  const { counts } = useGetOrdersCountByPerspective(perspective);
  const statusCounts = counts as OrderStatusCount;

  const allTabDetails = getOrderStatusTabDetails(statusCounts, perspective);
  const tabDetails = allTabDetails[currentOrderTab];
  const statusKey = tabDetails?.statusKey;

  const views = useOrdersSavedViews(perspective);
  const rows = views.rows;

  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { data: buyersData } = useGetOrganizationBuyers(
    perspective === 'BUYING'
  );
  const createdByOptions = useMemo(() => {
    const teammates = (buyersData?.users ?? []).map((buyer) => ({
      label: `${buyer.firstName} ${buyer.lastName}`,
      value: buyer.id,
    }));
    // buyListTeamCandidates excludes the caller (it's built for a team-share picker), so the
    // caller's own name is added back in here to let them filter to their own orders.
    const self = currentUser
      ? [
          {
            label: `${currentUser.firstName} ${currentUser.lastName}`,
            value: currentUser.id,
          },
        ]
      : [];
    return [...self, ...teammates];
  }, [buyersData?.users, currentUser]);

  const fieldOptions = useMemo(
    () => getOrderFilterFields(t, perspective, createdByOptions),
    [t, perspective, createdByOptions]
  );

  const filters = useMemo(
    () => buildOrderFiltersInput(rows, perspective),
    [rows, perspective]
  );

  const filtered = activeRowCount(rows, perspective) > 0;

  const {
    data: orders,
    sentinelRef,
    isLoading,
    isLoadingMore,
    error,
    page,
    refetch,
  } = usePaginatedQuery({
    fetchHook: useGetOrdersPage,
    select: (data) =>
      data?.orders as PaginatedResponse<PerspectiveOrder> | undefined,
    options: {
      pageSize: ORDERS_PAGE_SIZE,
      filters: { perspective, status: statusKey, filters },
    },
  });
  const firstPageFailed = Boolean(error) && page === 0;

  let listFallback = null;
  if (isLoading) {
    listFallback = <PageLoadingSpinner name={t('order.title')} />;
  } else if (firstPageFailed) {
    listFallback = (
      <RetryBanner
        message={
          filtered ? t('order.filter.load_error') : t('order.load_error')
        }
        retryLabel={t('general:general.retry')}
        onRetry={() => void refetch()}
        testId="orders"
      />
    );
  }

  const orderTabs = Object.values(allTabDetails).map(
    (tabDetail: TabDetail) => ({
      label: t(tabDetail.labelKey),
      count: tabDetail.count,
      link: tabDetail.link,
      component: listFallback ?? (
        <OrderList
          perspective={perspective}
          noOrderTitle={t('order.no_orders_found')}
          orders={orders}
          noOrderMessage={t(tabDetail.noOrdersMessageKey)}
          totalCount={tabDetail.count}
          filtered={filtered}
          isLoadingMore={isLoadingMore}
          sentinelRef={sentinelRef}
          loadMoreFailed={Boolean(error) && page > 0}
          onRetryLoadMore={() => void refetch()}
        />
      ),
    })
  );
  return (
    <div className="w-full flex flex-col gap-8">
      <title>{t('order.title')}</title>
      <OrdersToolbar
        fieldOptions={fieldOptions}
        perspective={perspective}
        views={views}
      />
      <OrdersFilterReadout
        rows={rows}
        fieldOptions={fieldOptions}
        perspective={perspective}
      />
      <div className="rounded-lg ">
        <div className="flex items-center gap-2 overflow-x-auto">
          <Tabs
            title={t('order.title')}
            subtitle={<PerspectiveCaption perspective={perspective} />}
            tabs={orderTabs}
            onTabChange={() => {}}
            basePath={basePath}
          />
        </div>
      </div>
    </div>
  );
}

function sumAllOrderCounts(statusCount: OrderStatusCount) {
  return (
    (statusCount?.draft || 0) +
    (statusCount?.pendingSellerConfirmation || 0) +
    (statusCount?.confirmed || 0) +
    (statusCount?.shipped || 0) +
    (statusCount?.delivered || 0) +
    (statusCount?.cancelled || 0) +
    (statusCount?.blocked || 0)
  );
}

const getOrderStatusTabDetails = (
  statusCount: OrderStatusCount,
  perspective: TradePerspective
): OrderStatusTabDetails => {
  const isSelling = perspective === 'SELLING';
  const roleKey = isSelling ? 'seller' : 'buyer';
  const allTabs: OrderStatusTabDetails = {
    all: {
      labelKey: 'order.all_orders',
      statusKey: undefined,
      link: 'all',
      count: sumAllOrderCounts(statusCount),
      noOrdersMessageKey: `order.${roleKey}.no_orders_message`,
    },
    ...(isSelling && {
      draft: {
        labelKey: 'order.draft_orders',
        statusKey: 'DRAFT' as OrderStatus,
        link: 'draft',
        count: statusCount?.draft || 0,
        noOrdersMessageKey: 'order.seller.no_draft_orders_message',
      },
    }),
    'pending-seller-confirmation': {
      labelKey: isSelling ? 'order.to_confirm' : 'order.awaiting_confirmation',
      statusKey: 'PENDING_SELLER_CONFIRMATION',
      link: 'pending-seller-confirmation',
      count: statusCount?.pendingSellerConfirmation || 0,
      noOrdersMessageKey: `order.${roleKey}.no_awaiting_confirmation_orders_message`,
    },
    confirmed: {
      labelKey: isSelling ? 'order.to_fulfill' : 'order.to_receive',
      statusKey: 'CONFIRMED',
      link: 'confirmed',
      count: statusCount?.confirmed || 0,
      noOrdersMessageKey: `order.${roleKey}.no_confirmed_orders_message`,
    },
    shipped: {
      labelKey: 'order.shipped_orders',
      statusKey: 'SHIPPED',
      link: 'shipped',
      count: statusCount?.shipped || 0,
      noOrdersMessageKey: 'order.no_shipped_orders_message',
    },
    delivered: {
      labelKey: 'order.delivered_orders',
      statusKey: 'DELIVERED',
      link: 'delivered',
      count: statusCount?.delivered || 0,
      noOrdersMessageKey: 'order.no_delivered_orders_message',
    },
    cancelled: {
      labelKey: 'order.cancelled_orders',
      statusKey: 'CANCELLED',
      link: 'cancelled',
      count: statusCount?.cancelled || 0,
      noOrdersMessageKey: 'order.no_cancelled_orders_message',
    },
    blocked: {
      labelKey: 'order.blocked_orders',
      statusKey: 'BLOCKED',
      link: 'blocked',
      count: statusCount?.blocked || 0,
      noOrdersMessageKey: 'order.no_blocked_orders_message',
    },
  };

  return allTabs;
};
